<template>
  <!-- sf-app: Vuetify v-app benzeri - flexbox container -->
  <div v-if="isAuthenticated" id="wrapper" class="sf-app">
    <!-- sf-app-bar: Header alanı -->
    <t-header class="sf-app-bar"></t-header>

    <!-- sf-navigation-drawer: Sidebar -->
    <t-side-nav class="sf-navigation-drawer"></t-side-nav>

    <!-- Mobile overlay -->
    <div class="side-overly" @click="closeMobileMenu"></div>

    <!-- sf-main: Ana içerik wrapper (flex column) -->
    <div class="sf-main page-content">
      <!-- sf-container: İçerik alanı (flex: 1 ile büyüyecek) -->
      <main class="sf-container" :class="{ 'no-padding-top': isHomePage, 'sf-container--full': isFullWidth }">
        <router-view />
      </main>

      <!-- sf-footer: Footer wrapper (semantic tag component içinde) -->
      <div v-if="!isMessagesPage" class="sf-footer">
        <t-footer :isCompanyPage="false"></t-footer>
      </div>
    </div>

    <!-- Mandatory Completion Modal -->
    <mandatory-completion-modal :visible="showMandatoryModal" @closed="onMandatoryModalClosed" />
  </div>
</template>

<script>
import Header from "@/view/layouts/user/Header";
import SideNav from "@/view/layouts/user/SideNav";
import Footer from "@/view/layouts/shared/components/Footer.vue";
import MandatoryCompletionModal from "@/view/components/modal/MandatoryCompletionModal.vue";
import { mapGetters } from 'vuex';
import { FETCH_MANDATORY_COMPLETIONS } from '@/core/services/store/mandatory.module';
import { LOGOUT, GET_USER_WITH_TOKEN, } from "@/core/services/store/auth.module";
import bridge from "@/core/services/bridge.service";
const ls = new SecureLS({ encodingType: "aes", isCompression: true });
import SecureLS from "secure-ls";

export default {
  name: "UserLayout",
  components: {
    "t-header": Header,
    "t-footer": Footer,
    "t-side-nav": SideNav,
    "mandatory-completion-modal": MandatoryCompletionModal,
  },
  computed: {
    ...mapGetters('mandatory', ['hasMandatoryItems', 'notices', 'assignments']),
    isMessagesPage() {
      return this.$route.name === 'User.messages';
    },
    isHomePage() {
      return this.$route.name === 'user.index' || this.$route.name === 'User.index';
    },
    isFullWidth() {
      return this.isHomePage;
    }
  }, data() {
    return {
      isAuthenticated: false,
      lastApiCallTime: 0,
      apiCallInterval: 6000, // 6 saniye
      showMandatoryModal: false,
    };
  },
  mounted() {
    let token = this.secureLocalStorage("token");
    let access_token = new URLSearchParams(window.location.search).get("token");

    if (access_token) {
      this.$store
        .dispatch(GET_USER_WITH_TOKEN, {
          url: "api/clients/authorization-token?token=" + access_token,
        })
        .then((response) => {
          if (!response) {
            this.$router.push({ path: "/login" });
            return;
          }
          const roles = response?.roles ?? [];
          if (roles.length > 0) {
            const mergedPermissions = this.mergeUniquePermissions(roles);
            ls.set("permissions", mergedPermissions);
            ls.set("roleCount", roles.length);
          }

          this.isAuthenticated = true;

          // Token ile giriş (SSO geri dönüşü) — Login.vue'deki akışla aynı şekilde
          // native uygulamaya bildir; aksi halde uygulama oturumu saklamaz ve
          // push token kaydı yapılmaz.
          bridge.send("LOGIN_SUCCESS", {
            token: response.access_token,
            subdomain: response.company_name || "",
            userId: response.id,
            name: response.name || "",
            email: response.email || "",
          });

          const query = new URLSearchParams(window.location.search);
          const type = query.get("type");
          const id = query.get("id");

          const routes = {
            assignment: () => id && { name: "user.assignment-detail", params: { id } },
            education: () => id && { name: "user.education-detail", params: { id } },
            "education-category": () => ({ path: "/education-category" }),
            "my-certification": () => ({ path: "/my-certification" }),
          };

          const route = routes[type]?.();

          // URL'den token parametresini temizle
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('token');
          cleanUrl.searchParams.delete('type');
          cleanUrl.searchParams.delete('id');
          window.history.replaceState({}, document.title, cleanUrl.toString());

          if (route) {
            this.$router.push(route);
          } else {
            this.$router.push({ path: "/dashboard" });
          }

        });
    } else {
      token ? (this.isAuthenticated = true) : null;
      let self = this;
      if (!token) {
        let url = window.location.href;
        if (!window.localStorage.getItem("redirect_url")) {
          window.localStorage.setItem("redirect_url", self.$route.fullPath);
        }
        this.$store
          .dispatch(LOGOUT)
          .then(() =>
            this.$router.push({
              name: "login",
              query: { redirect: self.$route.fullPath },
            })
          );
      } else {
        // Kullanıcı giriş yapmışsa mandatory completions kontrolü yap
        this.checkMandatoryCompletions();
      }
    }
  },
  watch: {
    '$route'(to, from) {
      if (this.isAuthenticated) {
        this.checkMandatoryCompletions();
      }
    }
  },
  methods: {
    mergeUniquePermissions(roles) {
      const permissionMap = new Map();

      roles.forEach((role) => {
        if (roles.length > 1 && role.name === "user") {
          return;
        }
        role?.permissions?.forEach((permission) => {
          if (!permissionMap.has(permission.name)) {
            permissionMap.set(permission.name, permission);
          }
        });
      });
      return Array.from(permissionMap.values());
    },
    async checkMandatoryCompletions() {
      if (this.$route.name === 'user.assignment-detail') {
        // eğitim görüntüleme-izleme sayfasında modal gösterilmesin
        return;
      }

      const currentTime = Date.now();
      const timeSinceLastCall = currentTime - this.lastApiCallTime;

      // Eğer son istek X saniyeden az süre önce yapıldıysa bekle
      if (timeSinceLastCall < this.apiCallInterval) {
        const waitTime = this.apiCallInterval - timeSinceLastCall;

        setTimeout(() => {
          this.makeMandatoryCompletionsRequest();
        }, waitTime);
        return;
      }

      // Rate limit kontrolü geçildi, isteği yap
      await this.makeMandatoryCompletionsRequest();
    },

    async makeMandatoryCompletionsRequest() {
      try {
        this.lastApiCallTime = Date.now();
        await this.$store.dispatch(`mandatory/${FETCH_MANDATORY_COMPLETIONS}`);

        // Eğer zorunlu tamamlama verisi varsa modal'ı aç
        if (this.$store.getters['mandatory/notices'].length > 0 || this.$store.getters['mandatory/assignments'].length > 0) {
          this.openMandatoryModal();
        }
      } catch (error) {
        console.error('Mandatory completions API error:', error);
      }
    },
    openMandatoryModal() {
      this.showMandatoryModal = true;
    },
    onMandatoryModalClosed() {
      this.showMandatoryModal = false;
    },
    closeMobileMenu() {
      const wrapper = document.getElementById('wrapper');
      if (wrapper && wrapper.classList.contains('mobile-active')) {
        wrapper.classList.remove('mobile-active');
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   Vuetify Benzeri Layout Sistemi
   ============================================ */

/* Ana Container: Flexbox + Full Height */
.sf-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f7fb;
}

/* Main Content Wrapper: Header altında kalan tüm alan */
.sf-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Flex children için önemli */

  /* Sidebar için margin (mevcut CSS'ten gelecek: .side-nav ~ .page-content) */
  /* Header için spacing yok (header zaten fixed/sticky) */
}

/* İçerik Container: Büyüyen alan */
.sf-container {
  flex: 1 1 auto;
  width: 100%;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 12px;
  padding-left: 60px;
  padding-right: 60px;
  box-sizing: border-box;
}

/* Anasayfa: Full width, padding yok */
.sf-container--full {
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
}

/* Anasayfa: Üstte padding yok */
.sf-container.no-padding-top {
  padding-top: 0;
}

/* Footer: Her zaman en altta */
.sf-footer {
  flex-shrink: 0;
  margin-top: auto;
  width: 100%;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  padding: 20px 40px;
  box-sizing: border-box;
}

/* ============================================
   Responsive: Mobile & Tablet
   ============================================ */

/* Mobile/Tablet (1220px altı) - global .page-content margin override */
@media (max-width: 1220px) {
  .sf-main.page-content {
    margin-top: 0 !important;
    margin-left: 0 !important;
  }
}

/* Tablet (1024px altı) */
@media (max-width: 1024px) {
  .sf-container:not(.sf-container--full) {
    padding-left: 30px;
    padding-right: 30px;
  }

  .sf-footer {
    padding: 16px 30px;
  }
}

/* Small Tablet / Mobile (768px altı) */
@media (max-width: 768px) {
  .sf-container:not(.sf-container--full) {
    padding-left: 16px;
    padding-right: 16px;
  }

  .sf-footer {
    padding: 12px 16px;
  }
}

/* Small Mobile (480px altı) */
@media (max-width: 480px) {
  .sf-container:not(.sf-container--full) {
    padding-left: 12px;
    padding-right: 12px;
  }

  .sf-footer {
    padding: 10px 12px;
  }
}
</style>
